import {
  ChangeEvent, useEffect, useMemo,
} from 'react';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { Mixpanel } from 'mixpanel';
import useMaskedInput from 'hooks/useMaskedInput';
import Button from 'components/Button';
import MaskInput from 'components/MaskedInput';
import Checkbox from 'components/Checkbox';
import useFeasibilityPlant from 'api/hooks/useFeasibilityPlant';
import useUser from 'api/hooks/useUser';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';
import { cn } from 'utils/cn';

interface Props {
  setActiveTab: (value: number) => void;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
  isPDF?: boolean;
}

const PlantSetUpFeasibility = ({
  setActiveTab,
  setValue,
  formWatch,
  isPDF,
}: Props) => {
  const { t } = useTranslation();
  const { data: user } = useUser();

  const { data: feasibilityPlantData } = useFeasibilityPlant();

  const {
    averageInflow: formaAerageInflow,
    processSteps,
  } = formWatch('plant');

  const {
    value: averageInflow,
    setValue: setAverageInflow,
    clean: cleanAverageInflow,
  } = useMaskedInput({
    defaultValue: '1000',
    value: formaAerageInflow,
    onChangeCallback: (value) => setValue('plant.averageInflow', value),
    isCleanValue: true,
  });

  useEffect(() => {
    const checkboxes = feasibilityPlantData?.wastewater?.map(
      (value: string) => ({
        title: value,
        isSptepsCheck: false,
        isAlkaliCheck: false,
      }),
    );

    setValue('plant.processSteps', checkboxes);
  }, [feasibilityPlantData]);

  const handleNext = (nextStep: number) => () => {
    setActiveTab(nextStep);
    Mixpanel.track('Feasibility Check Step 3', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const isProcessCheck = useMemo(
    () => processSteps?.some((value) => value.isSptepsCheck),
    [processSteps],
  );

  const disabledState = useMemo(
    () => !averageInflow || !isProcessCheck,
    [averageInflow, isProcessCheck],
  );

  const errorAverageInflowHandle = useMemo(() => {
    if (Number(cleanAverageInflow) < 1) {
      return t('minimumPossibleValue') + ' is 1';
    }

    if (Number(cleanAverageInflow) > 1000000) {
      return t('maximumPossibleValue') + ' is 1\'000\'000';
    }

    return '';
  }, [averageInflow]);

  const handleOnProcessCheckbox =
    (title: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const nextCheckboxes = processSteps?.map((checkbox) => {
        if (checkbox.title === title) {
          return {
            ...checkbox,
            isSptepsCheck: e.target.checked,
            ...(e.target.checked ? {} : { isAlkaliCheck: false }),
          };
        }

        return checkbox;
      });

      setValue('plant.processSteps', nextCheckboxes);
    };

  const handleOnAlkaliCheckbox =
    (title: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const nextCheckboxes = processSteps.map((checkbox) => {
        if (checkbox.title === title) {
          return {
            ...checkbox,
            isAlkaliCheck: e.target.checked,
          };
        }

        return checkbox;
      });

      setValue('plant.processSteps', nextCheckboxes);
    };

  return (
    <form>
      <div className="border border-primitiveGrey p-8">
        <div className="w-1/2 md:border-b md:border-r-0 pr-8 md:w-full md:pr-0 break-inside-avoid">
          <div className="my-6 w-full">
            <MaskInput
              className="w-full"
              titleClassName="top-[-9px] left-[8px]"
              title={`${t('averageInflow')}*`}
              onChange={setAverageInflow}
              value={averageInflow}
              redBorder={errorAverageInflowHandle.length > 0}
              decimalLimit={0}
            />
            <span className="text-[#FF3B30] text-xs ml-1 mt-1 h-2 flex">
              {errorAverageInflowHandle.length > 0 && (
                <span>{errorAverageInflowHandle}</span>
              )}{' '}
            </span>
          </div>
        </div>
        <div className={'mb-[40px] w-full overflow-x-auto'}>
          <img
            src='/plantSetUp.png'
            className={cn(
              { 'w-full h-auto': isPDF },
              { 'h-[200px] max-w-none': !isPDF },
            )}
            style={{ aspectRatio: 1241/216 }}
            alt="Image"
          />
        </div>
        <div className='flex w-full sm:flex-col'>
          <div className={cn(
            'flex flex-col justify-end items-end mr-10 md:items-start',
            { 'items-start': isPDF },
          )}>
            <div className="mb-[16px]">{t('processSteps')}</div>
            {processSteps?.map((value) => (
              <div key={value.title} className={cn(
                'flex mb-[12px] h-6 md:flex-row-reverse break-inside-avoid',
                { 'flex-row-reverse': isPDF },
              )}>
                <div className="mr-2">{value.title}</div>
                <Checkbox
                  name={value.title}
                  onChange={handleOnProcessCheckbox(value.title)}
                  checked={value.isSptepsCheck}
                  className={cn(
                    'md:mr-2',
                    { 'mr-2': isPDF },
                  )}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col justify-end items-start mr-10">
            <div className="mb-[16px]">
              {t('alkaliDosingPoint')}{' '}
              <span data-tooltip-id="dosingPoint" className="cursor-default font-black text-[#007AC1]">
                ⓘ
              </span>
            </div>
            <Tooltip
              id="dosingPoint"
              place="bottom"
              className="tooltip"
              variant="light"
              noArrow
              style={{ maxWidth: '345px' }}
            >
              <div>{t('alkaliDosingPointText')}</div>
            </Tooltip>
            {processSteps?.map((value, i) => (
              <div key={value.title + i} className={cn(
                'flex mb-[12px] h-6 md:flex-row-reverse md:items-end break-inside-avoid',
                { 'flex-row-reverse': isPDF },
              )}>
                <div className={cn({ 'hidden md:block mr-2': !isPDF })}>{value.title}</div>
                <Checkbox
                  type="checkbox"
                  name={value.title}
                  className={cn(
                    'md:mr-2',
                    { 'mr-2': isPDF },
                  )}
                  disabled={!value.isSptepsCheck}
                  onChange={handleOnAlkaliCheckbox(value.title)}
                  checked={value.isAlkaliCheck}
                />
              </div>
            ))}
          </div>
        </div>
        {!isPDF && (
          <div className="mt-8 flex justify-center md:mt-6">
            <Button
              onClick={() => setActiveTab(2)}
              className="mr-4"
              btnType="secondary"
            >
              {t('back')}
            </Button>
            <Button onClick={handleNext(6)} className="mr-4" btnType="secondary">
              {t('skipToBrief')}
            </Button>
            <Button disabled={disabledState} onClick={handleNext(4)}>
              {t('next')}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

export default PlantSetUpFeasibility;
